import React, { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ErrorBanner, PageShell } from "../components/PageShell";
import { FieldRow, TextField } from "../components/FormFields";
import { Button, Input } from "../components/fulkra";
import { api, formatApiError, withRetry } from "../lib/api";

const REQUIRED_FIELDS = ["voorletters","achternaam","geboortedatum","email","telefoon","straat","huisnummer","postcode","plaats"];
const FIELD_LABELS = {
  voorletters: "Voorletters", achternaam: "Achternaam", geboortedatum: "Geboortedatum",
  email: "E-mailadres", telefoon: "Telefoonnummer", straat: "Straat",
  huisnummer: "Huisnummer", postcode: "Postcode", plaats: "Plaats",
};

function validateEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
function validatePostcode(v) { return /^\d{4}\s?[A-Za-z]{2}$/.test(v); }

export default function AanmeldPage() {
  const { aanmeldcode } = useParams();
  const navigate = useNavigate();
  const fieldRefs = useRef({});
  const [form, setForm] = useState({
    voorletters: "", achternaam: "", geboortedatum: "", email: "", telefoon: "",
    straat: "", huisnummer: "", huisnummer_toevoeging: "", postcode: "", plaats: "",
    avg_toestemming: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (submitAttempted) {
      setFieldErrors((cur) => {
        const next = { ...cur };
        delete next[k];
        return next;
      });
    }
  }

  function collectFieldErrors() {
    const errs = {};
    for (const k of REQUIRED_FIELDS) {
      if (!String(form[k] || "").trim()) errs[k] = "Verplicht";
    }
    if (form.email && !validateEmail(form.email)) errs.email = "Geen geldig e-mailadres";
    if (form.postcode && !validatePostcode(form.postcode)) errs.postcode = "Postcode in formaat 1234 AB";
    if (!form.avg_toestemming) errs.avg_toestemming = "Geef akkoord op de privacy-voorwaarden";
    return errs;
  }

  function scrollToFirstError(errs) {
    const order = [...REQUIRED_FIELDS, "avg_toestemming"];
    for (const k of order) {
      if (errs[k] && fieldRefs.current[k]) {
        fieldRefs.current[k].scrollIntoView({ behavior: "smooth", block: "center" });
        const input = fieldRefs.current[k].querySelector("input, textarea");
        if (input) setTimeout(() => input.focus(), 300);
        return;
      }
    }
  }

  async function submit() {
    setError(null);
    setSubmitAttempted(true);
    const errs = collectFieldErrors();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      const ontbrekend = Object.keys(errs).map((k) => FIELD_LABELS[k] || k).join(", ");
      setError({ message: `Niet alle velden zijn (correct) ingevuld: ${ontbrekend}.` });
      scrollToFirstError(errs);
      return;
    }
    setSubmitting(true);
    try {
      const res = await withRetry(() => api.post(`/werknemer/aanmeld/${aanmeldcode}`, form));
      const token = res.data.progressie_token;
      // Naar de gegevenspagina, niet rechtstreeks naar de vragenlijst. Sinds blok 1 gaat
      // er bij zelfaanmelding geen welkomstmail meer weg; zou iemand hier op de
      // vragenlijst landen en die nu even niet willen invullen, dan hield hij niets over.
      // Op de gegevenspagina kiest hij zelf: nu invullen, of de link later per mail.
      if (token) navigate(`/gegevens/${token}`);
      else setError({ message: "Aanmelding gelukt, maar geen toegangstoken ontvangen. Neem contact op." });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function renderField(name, label, type = "text", placeholder = "", required = true) {
    const err = fieldErrors[name];
    return (
      <div ref={(el) => { fieldRefs.current[name] = el; }} style={{ display: "grid", gap: "var(--ruimte-3)" }}>
        <label htmlFor={`field-${name}`} style={{ font: "var(--text-label)", color: "var(--text-strong)" }}>
          {label} {required ? <span style={{ color: "var(--alarm)" }}>*</span> : null}
        </label>
        <Input
          id={`field-${name}`}
          type={type}
          value={form[name]}
          onChange={(e) => up(name, e.target.value)}
          placeholder={placeholder}
          data-testid={`field-${name}`}
          invalid={Boolean(err)}
        />
        {err ? (
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-alarm)" }} data-testid={`err-${name}`}>{err}</p>
        ) : null}
      </div>
    );
  }

  return (
    <PageShell title="Aanmelden">
      <div data-testid="aanmeld-page" style={{ display: "grid", gap: "var(--ruimte-7)" }}>
        <header>
          <h1 style={{ font: "var(--text-h2)", letterSpacing: "var(--ls-kop)", color: "var(--text-strong)" }}>Welkom bij Fulkra Works</h1>
          <p style={{ marginTop: "var(--ruimte-4)", fontSize: "var(--fs-body)", color: "var(--text-soft)" }}>
            Vul uw gegevens in om aan het programma deel te nemen. Alle velden zijn nodig zodat uw werkgever uw deelname kan registreren.
          </p>
        </header>

        <ErrorBanner error={error} />

        <div style={{ display: "grid", gap: "var(--ruimte-6)" }}>
          {renderField("voorletters", "Voorletters", "text", "bv. M.J.")}
          {renderField("achternaam", "Achternaam")}
          {renderField("geboortedatum", "Geboortedatum", "date")}
          {renderField("email", "E-mailadres", "email", "naam@bedrijf.nl")}
          {renderField("telefoon", "Telefoonnummer", "tel", "06-12345678")}
          {renderField("straat", "Straat")}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "var(--ruimte-5)" }}>
            {renderField("huisnummer", "Huisnummer")}
            <div ref={(el) => { fieldRefs.current.huisnummer_toevoeging = el; }} style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <label htmlFor="field-huisnummer_toevoeging" style={{ font: "var(--text-label)", color: "var(--text-strong)" }}>Toevoeging</label>
              <Input
                id="field-huisnummer_toevoeging"
                type="text"
                value={form.huisnummer_toevoeging}
                onChange={(e) => up("huisnummer_toevoeging", e.target.value)}
                placeholder="bv. A"
                data-testid="field-huisnummer_toevoeging"
              />
            </div>
          </div>
          {renderField("postcode", "Postcode", "text", "1234 AB")}
          {renderField("plaats", "Plaats")}
        </div>

        <div ref={(el) => { fieldRefs.current.avg_toestemming = el; }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "var(--ruimte-5)", minHeight: "var(--touch-min)", cursor: "pointer" }} data-testid="avg-aanmeld-label">
            <input
              type="checkbox"
              data-testid="avg-aanmeld-checkbox"
              checked={form.avg_toestemming}
              onChange={(e) => up("avg_toestemming", e.target.checked)}
              style={{
                marginTop: 2, marginLeft: 4, width: 20, height: 20, accentColor: "var(--actie)",
                borderRadius: "var(--radius-klein)", flexShrink: 0,
                outline: fieldErrors.avg_toestemming ? "2px solid var(--alarm)" : "none",
              }}
            />
            <span style={{ fontSize: "var(--fs-body)", color: "var(--text-body)" }}>
              Ik ga akkoord met de{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-action)", textDecoration: "underline", textUnderlineOffset: 2 }}>
                privacy-voorwaarden
              </Link>
              {" "}en geef toestemming voor verwerking van mijn gegevens.
            </span>
          </label>
          {fieldErrors.avg_toestemming ? (
            <p style={{ marginLeft: "var(--ruimte-9)", marginTop: "var(--ruimte-3)", fontSize: "var(--fs-sm)", color: "var(--text-alarm)" }} data-testid="err-avg">{fieldErrors.avg_toestemming}</p>
          ) : null}
        </div>

        <Button
          size="touch"
          onClick={submit}
          disabled={submitting}
          data-testid="btn-aanmeld-submit"
        >
          {submitting ? "Bezig met aanmelden…" : "Aanmelden en doorgaan"}
        </Button>
      </div>
    </PageShell>
  );
}
